import type { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { cn } from '@/lib/utils.js';

export type DataTableProps = {
  caption: string;
  head: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Column widths, one per column, as CSS lengths — `['40%', '12%', …]`.
   *
   * Given, the table stops sizing itself to its own content and takes these
   * instead. That is what makes SEVERAL tables on one page line up: a page of
   * per-URL splits auto-sized every table independently, so the Status column
   * landed in a different place in each one and the eye had to re-find every
   * column on the way down. One table on a screen has no such problem and
   * should keep sizing itself, which is why this is opt-in.
   */
  columns?: readonly string[];
};

/**
 * A table on its own surface.
 *
 * Wrapped in a bordered, rounded panel rather than sitting flush against the
 * page: at a glance the reader needs to see where the data begins and ends,
 * and a rule-lined table on a bare background reads as loose text. The panel
 * clips its own corners and owns the horizontal scroll, so wide content never
 * makes the page body scroll sideways.
 */
export function DataTable({ caption, head, children, className, columns }: DataTableProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-(--radius-lg) border border-rule bg-surface-raised shadow-(--shadow-raised)',
        className,
      )}
    >
      {/* No `.scroll-x` here: `Table` already wraps itself in one, and nesting
          two scroll containers gives the inner one a scrollbar the outer one
          then hides. */}
      {/* `table-fixed` is what makes a colgroup binding rather than advisory:
          without it the browser is free to grow a column to fit its content
          and the widths become suggestions. */}
      <Table className={columns === undefined ? undefined : 'table-fixed'}>
        <caption className="sr-only">{caption}</caption>
        {columns !== undefined && (
          <colgroup>
            {columns.map((width, index) => (
              // The index IS the identity here: these are positions in a
              // fixed column order, not a list that can be reordered.
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
        )}
        <TableHeader>
          <TableRow className="hover:bg-transparent">{head}</TableRow>
        </TableHeader>
        <TableBody>{children}</TableBody>
      </Table>
    </div>
  );
}

export type DataRowProps = {
  children: ReactNode;
  /**
   * Makes the whole row activate, by pointer and by keyboard.
   *
   * Anything interactive nested inside a clickable row MUST call
   * `stopPropagation` on its own handler, or clicking a delete button would
   * also navigate. `RowActions` below does that for you.
   */
  onActivate?: () => void;
  /**
   * Required when `onActivate` is set: what activating this row does.
   *
   * This becomes the row's `aria-label`, which REPLACES its natural accessible
   * name (the cell text). So it must contain the value that identifies the row
   * — `Open group Acme`, not `Open group` — or the row becomes unfindable by
   * name for a screen reader user AND for `getByRole('row', { name })`.
   */
  activateLabel?: string;
  selected?: boolean;
  className?: string;
};

export function DataRow({
  children,
  onActivate,
  activateLabel,
  selected,
  className,
}: DataRowProps) {
  const interactive = onActivate !== undefined;

  return (
    <TableRow
      {...(interactive
        ? {
            // A row is not natively focusable, so it is given a tab stop and
            // Enter/Space handling. Without this the row would be reachable by
            // mouse only, which fails WCAG 2.2 keyboard operability.
            tabIndex: 0,
            role: 'row',
            'aria-label': activateLabel,
            onClick: onActivate,
            onKeyDown: (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onActivate();
              }
            },
          }
        : {})}
      data-selected={selected === true ? '' : undefined}
      className={cn(
        interactive && 'cursor-pointer',
        selected === true && 'bg-surface-sunken',
        className,
      )}
    >
      {children}
    </TableRow>
  );
}

export type RowActionsProps = { children: ReactNode };

/**
 * Right-aligned action cluster for a row.
 *
 * Swallows click and keydown so the buttons inside never also activate the
 * row — the single easiest way to make a clickable row destructive by
 * accident.
 */
export function RowActions({ children }: RowActionsProps) {
  return (
    <div
      className="flex items-center justify-end gap-(--space-1)"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  );
}

export { TableCell, TableHead };
